/**
 * Google Publisher Tags (GPT) loader.
 *
 * Finds ad placeholders rendered by the `apc-ad-slot` block (any element with a
 * `data-ad-unit-code` attribute), waits for marketing consent, loads Google's
 * gpt.js, registers a Google Ad Manager slot per placeholder and displays the ads.
 *
 * The ad creatives, targeting and the ad units themselves all live in the
 * client's Google Ad Manager account. This script only references units that
 * already exist there via the full ad-unit path stored in `data-ad-unit-code`.
 */

const GPT_SRC = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';

const ADS_CONSENTED_CLASS = 'apc-ads-consented';
const HIDDEN_CLASS = 'hidden';
const REVEAL_TIMEOUT = 1500;
const CONSENT_POLL_INTERVAL = 100;
const CONSENT_POLL_TIMEOUT = 15000;

/**
 * Slots we have already registered, so the same placeholder is never added twice.
 * @type {Map<string, { slot: GoogleTagSlot; loaded: boolean; displayed?: boolean; refreshed?: boolean }>}
 */
const registeredSlots = new Map();

let started = false;
let servicesEnabled = false;
let gptRequested = false;
let consentApiRequested = false;

/* -------------------------------------------------------------------------- */
/* Consent                                                                    */
/*                                                                            */
/* Ad serving requires MARKETING consent. We read it from two sources so the  */
/* banners work regardless of which consent platform the store runs:          */
/*   1. Shopify's Customer Privacy API (built-in banner, or any CMP that       */
/*      feeds it) — marketingAllowed().                                        */
/*   2. A custom CMP contract: window.apcConsent.marketing + the               */
/*      'apc:marketing-consent-granted' event (see SHOP-78).                   */
/* -------------------------------------------------------------------------- */

/** @returns {boolean} true when the visitor has granted marketing consent. */
function marketingConsentGranted() {
  if (window.Shopify?.customerPrivacy?.marketingAllowed?.() === true) return true;
  if (window.apcConsent?.marketing === true) return true;
  return false;
}

/* -------------------------------------------------------------------------- */
/* GPT setup                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Returns the global googletag object, creating its command queue if gpt.js
 * hasn't loaded yet. The API methods are populated by gpt.js once it runs; we
 * only push to `cmd` before then, so callbacks always see a fully-formed object.
 * @returns {GoogleTag}
 */
function ensureGpt() {
  const existing = window.googletag;
  if (existing) return existing;

  // gpt.js fills in the API methods on this object once it loads.
  const created = /** @type {GoogleTag} */ (/** @type {any} */ ({ cmd: [] }));
  window.googletag = created;
  return created;
}

/** Loads gpt.js once. */
function loadGpt() {
  ensureGpt();

  if (!gptRequested) {
    gptRequested = true;
    const script = document.createElement('script');
    script.src = GPT_SRC;
    script.async = true;
    document.head.appendChild(script);
  }
}

/**
 * Parses a slot's `data-ad-sizes` into GPT sizes. Blank or "fluid" → responsive;
 * "150x500" → a fixed size; comma-separate to allow several (e.g. "fluid,300x250").
 * @param {string | undefined} raw
 * @returns {Array<string | number[]>}
 */
function parseSizes(raw) {
  if (!raw) return ['fluid'];

  const sizes = [];
  for (const part of raw.split(',')) {
    const token = part.trim().toLowerCase();
    if (token === 'fluid') {
      sizes.push('fluid');
    } else {
      const match = token.match(/^(\d+)x(\d+)$/);
      if (match) sizes.push([Number(match[1]), Number(match[2])]);
    }
  }

  return sizes.length ? sizes : ['fluid'];
}

/** Registers every placeholder that isn't registered yet, then displays them. */
function registerSlots() {
  const gpt = ensureGpt();
  const placeholders = /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('[data-ad-unit-code]'));

  gpt.cmd.push(() => {
    const pubads = gpt.pubads();

    // A re-rendered product grid (Algolia filters/sorting) replaces its banners
    // with fresh template clones, so the element a slot rendered into is gone.
    // Destroy those slots so the same placeholder id can be defined again.
    for (const [id, entry] of registeredSlots) {
      if (document.getElementById(id)) continue;
      gpt.destroySlots([entry.slot]);
      registeredSlots.delete(id);
    }

    for (const placeholder of placeholders) {
      const code = placeholder.dataset.adUnitCode;
      if (!code) continue;

      // Skip placeholders hidden on this breakpoint (e.g. a banner the merchant
      // hides on mobile) so we don't request an ad that can't be seen. Don't use
      // offsetParent here: it is null for position: fixed elements such as the
      // skyscraper, which would skip the slot exactly where it should show.
      if (!placeholder.getClientRects().length) continue;

      // GAM ad-unit paths must start with a slash; the codes from the client don't.
      const adUnitPath = code.startsWith('/') ? code : `/${code}`;

      // Give the placeholder a stable id GPT can render into.
      if (!placeholder.id) {
        placeholder.id = `gpt-${code.replace(/[^a-z0-9]+/gi, '-')}`;
      }

      // Don't register the same slot twice.
      if (registeredSlots.has(placeholder.id)) continue;

      const sizes = parseSizes(placeholder.dataset.adSizes);
      const slot = gpt.defineSlot(adUnitPath, sizes, placeholder.id)?.addService(pubads);
      if (!slot) continue;

      // Language targeting key-value (e.g. lang=nl), so GAM can target ads by
      // language. Key and value are set per slot by the apc-ad-slot snippet.
      const targetingKey = placeholder.dataset.adTargetingKey;
      const targetingValue = placeholder.dataset.adTargetingValue;
      if (targetingKey && targetingValue) slot.setTargeting(targetingKey, targetingValue);

      registeredSlots.set(placeholder.id, { slot, loaded: false });
    }

    // Enable Google Publisher services exactly once.
    if (!servicesEnabled) {
      pubads.addEventListener('slotRenderEnded', onSlotRenderEnded);
      gpt.enableServices();
      servicesEnabled = true;
    }

    // Ask Google to display each slot that hasn't been displayed yet.
    for (const entry of registeredSlots.values()) {
      if (entry.displayed) continue;
      entry.displayed = true;
      gpt.display(entry.slot);
    }

    // Refresh any slot that was created but hasn't loaded shortly after.
    scheduleRefreshFallback();
  });
}

/**
 * Marks a slot as loaded so CSS can fade it in and drop the fallback styling.
 * @param {GoogleTagSlotEvent} event
 */
function onSlotRenderEnded(event) {
  const slotId = event.slot.getSlotElementId();
  const entry = registeredSlots.get(slotId);
  if (entry) entry.loaded = true;

  const element = document.getElementById(slotId);
  if (!element) return;

  const isEmpty = event.isEmpty === true;
  element.classList.toggle('apc-ad-slot--empty', isEmpty);

  // Google returned no ad and there is no fallback banner: hide the slot so
  // its parent (e.g. a product grid cell) can collapse via `:has(.hidden)`.
  const hasFallback = element.classList.contains('apc-ad-slot--has-fallback');
  element.classList.toggle(HIDDEN_CLASS, isEmpty && !hasFallback);

  revealWhenReady(element, isEmpty);
}

/**
 * @param {HTMLElement} element
 * @param {boolean} isEmpty
 */
function revealWhenReady(element, isEmpty) {
  const reveal = () => element.classList.add('apc-ad-slot--loaded');

  if (isEmpty) {
    reveal();
    return;
  }

  const hooked = new WeakSet();
  const observer = new MutationObserver(hookIframes);
  const timer = setTimeout(finish, REVEAL_TIMEOUT);

  function finish() {
    clearTimeout(timer);
    observer.disconnect();
    reveal();
  }

  function hookIframes() {
    for (const iframe of element.querySelectorAll('iframe')) {
      if (hooked.has(iframe)) continue;
      hooked.add(iframe);
      iframe.addEventListener('load', finish, { once: true });
    }
  }

  observer.observe(element, { childList: true, subtree: true });
  hookIframes();
}

/** Refreshes each unloaded slot once, in case the first request didn't fill. */
function scheduleRefreshFallback() {
  const gpt = ensureGpt();

  window.setTimeout(() => {
    gpt.cmd.push(() => {
      for (const entry of registeredSlots.values()) {
        if (entry.loaded || entry.refreshed) continue;
        entry.refreshed = true;
        gpt.pubads().refresh([entry.slot]);
      }
    });
  }, 2000);
}

/* -------------------------------------------------------------------------- */
/* Skyscraper                                                                 */
/* -------------------------------------------------------------------------- */

const SKYSCRAPER_SELECTOR = '.apc-ad-slot--skyscraper';
const FOOTER_SELECTOR = '.shopify-section-group-footer-group';
const SKYSCRAPER_MEDIA = '(width > 103rem)';
let skyscraperObserved = false;

function observeSkyscraper() {
  if (skyscraperObserved) return;

  const footer = document.querySelector(FOOTER_SELECTOR);
  if (!footer || !document.querySelector(SKYSCRAPER_SELECTOR)) return;
  skyscraperObserved = true;

  const media = window.matchMedia(SKYSCRAPER_MEDIA);
  let frame = 0;

  const update = () => {
    frame = 0;
    const skyscraper = /** @type {HTMLElement | null} */ (document.querySelector(SKYSCRAPER_SELECTOR));
    if (!skyscraper) return;

    if (!media.matches) {
      skyscraper.style.removeProperty('--footer-offset');
      return;
    }
    if (!skyscraper.getClientRects().length) return;

    // Where the ad's bottom edge would be with no offset applied.
    const applied = parseFloat(skyscraper.style.getPropertyValue('--footer-offset')) || 0;
    const restingBottom = skyscraper.getBoundingClientRect().bottom + applied;
    const overlap = Math.max(0, restingBottom - footer.getBoundingClientRect().top + 20);
    skyscraper.style.setProperty('--footer-offset', `${overlap}px`);
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(update);
  };

  // Only track scrolling while the footer is on screen; off screen the offset is 0.
  const footerObserver = new IntersectionObserver(([entry]) => {
    if (entry?.isIntersecting) window.addEventListener('scroll', schedule, { passive: true });
    else window.removeEventListener('scroll', schedule);
    schedule();
  });

  const toggle = () => {
    if (media.matches) {
      footerObserver.observe(footer);
      window.addEventListener('resize', schedule);
      document.addEventListener('algolia:results-rendered', schedule);
    } else {
      footerObserver.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('algolia:results-rendered', schedule);
    }
    schedule();
  };

  media.addEventListener('change', toggle);
  toggle();
}

/**
 * Loads GPT and registers slots — the full ad setup.
 * @returns {boolean} Whether the setup ran.
 */
function start() {
  if (!document.querySelector('[data-ad-unit-code]') || window.Shopify?.designMode) return false;

  // Reveal consent-gated slots before registering: GPT skips placeholders with
  // no layout box, and `registerSlots` checks offsetParent.
  document.documentElement.classList.add(ADS_CONSENTED_CLASS);

  started = true;
  loadGpt();
  registerSlots();
  observeSkyscraper();
  return true;
}

// Algolia re-renders the product grid (filters, sorting, pagination) with fresh
// banner clones. The event fires just before the new grid is attached, so wait
// a frame and then register the new placeholders.
document.addEventListener('algolia:results-rendered', () => {
  if (!started) return;
  requestAnimationFrame(() => registerSlots());
});

/* -------------------------------------------------------------------------- */
/* Bootstrap                                                                  */
/* -------------------------------------------------------------------------- */

function ensureConsentApi() {
  if (consentApiRequested) return;

  const loadFeatures = window.Shopify?.loadFeatures;
  if (typeof loadFeatures !== 'function') return;

  consentApiRequested = true;
  loadFeatures.call(window.Shopify, [{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
    if (!error && marketingConsentGranted()) start();
  });
}

ensureConsentApi();

// React to consent changes from Shopify's banner.
document.addEventListener('visitorConsentCollected', (event) => {
  if (event?.detail?.marketingAllowed) start();
});

// Custom CMP contract (per SHOP-78), in case consent is exposed this way.
window.addEventListener('apc:marketing-consent-granted', () => {
  if (marketingConsentGranted()) start();
});

const consentPoll = window.setInterval(() => {
  ensureConsentApi();
  if (marketingConsentGranted() && start()) window.clearInterval(consentPoll);
}, CONSENT_POLL_INTERVAL);

window.setTimeout(() => window.clearInterval(consentPoll), CONSENT_POLL_TIMEOUT);
